import React, { useState } from 'react';
import { api } from '../utils/api';

/**
 * ChangePasswordModal
 * --------------------
 * Reusable modal that lets an authenticated user change their own password.
 * Used in Admin / Staff / Parent dashboards (and triggered automatically when
 * a user has `must_change_password=true` after a super-admin reset).
 *
 * Props:
 *  - open       : boolean — whether the modal is visible
 *  - onClose    : () => void — close handler (ignored when forced=true)
 *  - onSuccess  : () => void — called after a successful change (e.g. clear
 *                 the must_change_password flag locally / refresh user)
 *  - forced     : boolean — if true, the modal cannot be dismissed; intended
 *                 for the post-reset "you must change your password now" flow
 */
export const ChangePasswordModal = ({ open, onClose, onSuccess, forced = false }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    setSubmitting(true);
    try {
      await api.changeMyPassword(currentPassword, newPassword);
      // Reset state and bubble up
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (onSuccess) onSuccess();
      if (!forced && onClose) onClose();
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !forced && !submitting && onClose && onClose()}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        data-testid="change-password-modal"
      >
        <div className="modal-header">
          <h2>🔐 {forced ? 'Set a new password' : 'Change password'}</h2>
        </div>

        {forced && (
          <div
            style={{
              padding: '0.75rem 1rem',
              background: 'hsl(var(--muted))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              marginBottom: '1rem',
            }}
            data-testid="forced-change-notice"
          >
            Your password was reset by an administrator. Please choose a new password
            that only you know before continuing.
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-group">
            <label htmlFor="cp-current">
              {forced ? 'Temporary password (the one you just used to sign in)' : 'Current password'}
            </label>
            <input
              id="cp-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="change-password-current"
            />
          </div>

          <div className="form-group">
            <label htmlFor="cp-new">New password (min 8 characters)</label>
            <input
              id="cp-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              required
              data-testid="change-password-new"
            />
          </div>

          <div className="form-group">
            <label htmlFor="cp-confirm">Confirm new password</label>
            <input
              id="cp-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={8}
              autoComplete="new-password"
              required
              data-testid="change-password-confirm"
            />
          </div>

          {error && (
            <div
              style={{
                padding: '0.5rem 0.75rem',
                background: 'hsl(0 70% 95%)',
                border: '1px solid hsl(0 70% 80%)',
                borderRadius: '0.375rem',
                color: 'hsl(0 70% 35%)',
                fontSize: '0.875rem',
                marginBottom: '0.75rem',
              }}
              data-testid="change-password-error"
            >
              {error}
            </div>
          )}

          <div className="modal-actions">
            {!forced && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={submitting}
                data-testid="change-password-cancel-btn"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              data-testid="change-password-submit-btn"
            >
              {submitting ? 'Saving…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
